<template>
  <!-- Sidebar Onboarding -->
  <div class="flex flex-col flex-1 px-4 overflow-y-auto">
    <span class="my-3 border-gray-500" />

    <div class="flex space-x-3 w-full">
      <div class="w-[320px] h-full bg-white rounded-lg p-4">
        <div class="text-xl font-medium mb-4">
          {{ $t("Creation Steps") }}
        </div>
        <div
          v-for="(element, index) in selected_metrics.length > 1 ? steps : steps.filter((s) => s.name !== 'Weight Distribution')"
          :key="element"
          class="cursor-pointer">
          <div class="flex items-center space-x-2" @click="steps[index].status == 'current'">
            <div class="w-8 h-8 bg-[#E4E7EC] rounded-full flex items-center justify-center relative">
              <div
                v-if="steps[index].status == 'current' || element.status == 'complete'"
                :class="[{ '!bg-green-500 w-8 h-8': element.status == 'complete' }, { '!bg-purple-500': steps[index].status == 'current' }]"
                class="w-6 h-6 bg-transparent rounded-full" />

              <Check v-if="element.status == 'complete' && steps[index].status !== 'current'" class="w-5 h-5 text-white absolute" />
              <div
                v-if="element.status !== 'complete' || steps[index].status == 'current'"
                class="w-2 absolute h-2 bg-white rounded-full" />
            </div>
            <div class="text-sm">
              {{ element.name }}
            </div>
          </div>
          <div
            v-if="
              index < (selected_metrics.length > 1 ? steps.length - 1 : steps.filter((s) => s.name !== 'Weight Distribution').length - 1)
            "
            :class="[{ '!bg-green-500': element.status == 'complete' }]"
            class="h-16 ml-3.5 w-[2px] bg-[#E4E7EC]" />
        </div>
      </div>
      <div class="w-full bg-white p-4 rounded-lg relative">
        <div v-if="steps[0].status == 'current'">
          <div class="mt-8 text-2xl dark:text-white">
            {{ $t("Details") }}
          </div>
          <div class="flex items-end gap-4 mt-10">
            <TextInput
              v-model="score_name"
              :tooltip-info="$t('Enter name here')"
              :value="score_name"
              classes=""
              :validate-on-input="true"
              name="score_name"
              type="text"
              :label="$t('Name')"
              @input="score_name = $event.target.value" />
            <ComboBoxInput
              v-if="score_object_type"
              v-model:model-value="score_object_type"
              :items="$store.state.object_types"
              name="object_type"
              classes="w-full relative dark:text-white"
              label-attr="label"
              value-attr="type"
              :is-box-background="true"
              :label="$t('Object Type')"
              :tooltip-info="$t('Select object type here')"
              :show-label="true"
              :floating-label="true"
              :allow-null="false" />
          </div>

          <div class="mt-8 -space-y-px rounded-md shadow-sm">
            <div class="mb-2 ml-1 text-common-grey flex items-center">
              {{ $t("Description") }} ({{ $t("optional") }})
              <div class="p-0.5 w-4 h-4 bg-[#899399] flex items-center justify-center rounded-full text-white ml-2 mt-0.5 has-tooltip">
                ?
                <span
                  class="inline-flex ml-6 mt-14 text-common-grey items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                  {{ $t("Enter description here") }}</span>
              </div>
            </div>

            <TextArea
              v-if="scores_loaded"
              tooltip-info="Select object type here"
              label=""
              name="score_description"
              :value="score_description"
              @input="score_description = $event.target.value" />
          </div>
          <div class="absolute bottom-10 w-[95%] flex justify-between">
            <div type="button" class="cursor-pointer text-blue w-[80px] m-0 ml-3" @click="handleCancelClick">
              {{ $t("Cancel") }}
            </div>
            <button
              type="button"
              class="py-3 -mt-4"
              :disabled="!score_name"
              :class="[`primary-btn w-fit ml-3  ${!score_name ? 'bg-gray-400 dark:bg-gray-400' : ''}`]"
              @click="nextClick(0)">
              <ArrowDownToDot
                class="w-6 mr-2 absolute -ml-12 text-white"
                :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-90' : '-rotate-90']" />
              <span class="ml-10">{{ $t("Next Step") }}</span>
            </button>
            <!-- <button v-if="score_edit" type="button" class="primary-btn w-[140px] ml-3" @click="submitClick">Submit changes</button> -->
          </div>
        </div>

        <div v-else-if="steps[1].status == 'current'">
          <div>
            <div class="my-8 text-2xl dark:text-white">
              {{ $t("Display") }}
            </div>

            <div class="p-0.5 w-fit space-x-4 rounded-full bg-[#F5F8FA] border border-[#B3CAD6] flex items-center">
              <div
                :class="{ 'bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] text-white': add_aggregation }"
                class="p-2 px-4 rounded-full cursor-pointer"
                @click="
                  () => {
                    add_aggregation = true
                    aggregation_method = 'sum'
                  }
                ">
                {{ $t("Create as a Group KPI") }}
              </div>
              <div
                :class="{ 'bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] text-white': !add_aggregation }"
                class="p-2 rounded-full cursor-pointer px-4"
                @click="add_aggregation = false">
                {{ $t("Create as a KPI") }}
              </div>
            </div>

            <SelectInput
              v-if="add_aggregation"
              v-model:current-item="aggregation_method"
              :value="aggregation_method"
              :show-label="true"
              :allow-null="!$route.query?.redirect_url"
              :items="metrics_aggregation_methods"
              name="agmeth"
              value-attr="value"
              label-attr="name"
              :placeholder="$t('Aggregation method')"
              label=""
              classes="w-1/2 mt-4"
              input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light " />
          </div>

          <div class="absolute bottom-10 w-[95%] flex justify-between">
            <div type="button" class="cursor-pointer text-blue w-fit m-0 ml-3" @click="backClick(1)">
              {{ $t("Previous Step") }}
            </div>
            <button
              type="button"
              class="py-3 -mt-4"
              :disabled="!score_name"
              :class="[`primary-btn w-fit ml-3  ${false ? 'bg-gray-400 dark:bg-gray-400' : ''}`]"
              @click="nextClick(1)">
              <ArrowDownToDot
                class="w-6 mr-2 absolute -ml-12 text-white"
                :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-90' : '-rotate-90']" />
              <span class="ml-10">{{ $t("Next Step") }}</span>
            </button>
            <!-- <button v-if="score_edit" type="button" class="primary-btn w-[140px] ml-3" @click="submitClick">Submit changes</button> -->
          </div>
        </div>

        <div v-else-if="steps[2].status == 'current'">
          <div class="flex">
            <div class="my-8 text-2xl dark:text-white">
              {{ $t("Metric Selection") }}
            </div>
          </div>

          <div>
            <AutoCompleteInput
              tooltip-info="Find metrics"
              class="w-[500px]"
              :data="
                $store.state.metrics.filter(
                  (m) =>
                    (score_object_type ? m.object_type == score_object_type : true) &&
                    (m.data_type === 'Number' || m.data_type === 'String' || m.data_type === 'Date')
                )
              "
              :label="$t('Search and add metrics')"
              :floating-label="true"
              @chosen="chooseMetric($event)" />
          </div>

          <div class="flex space-x-2 items-center mt-4">
            <div class="text-common-grey">
              {{ $t("All metrics have an equal impact on the score") }}
            </div>

            <Switch
              v-model="equal_weight"
              :class="[
                equal_weight ? 'bg-blue' : 'bg-gray-200 dark:bg-dark-screen-background',
                'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
              ]">
              <span
                aria-hidden="true"
                :class="[
                  equal_weight && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-5'
                    : equal_weight
                      ? 'translate-x-5'
                      : 'bg-common-grey translate-x-0',
                  'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                ]" />
            </Switch>
          </div>

          <div v-if="metric_percentages.length < 2 || equal_weight" class="flex flex-wrap mt-2 space-x-2">
            <div v-for="metric in selected_metrics" :key="metric.id">
              <div class="bg-[#B91979] p-2 rounded-full w-fit text-white flex space-x-2 items-center">
                {{ metric.name }}
                <X
                  class="w-6 h-6 ml-2 cursor-pointer"
                  color="white"
                  @click="selected_metrics = selected_metrics.filter((m) => m.id !== metric.id)" />
              </div>
            </div>
          </div>

          <SelectInput
            v-if="!equal_weight && metric_percentages.length > 1"
            :value="weight_option"
            :items="weight_options"
            placeholder="Select option"
            class="w-44"
            input-classes="w-44 mt-4 mb-2 appearance-none text-left block px-3 pt-2 pb-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            @update:currentItem="weight_option = $event" />

          <div v-if="!equal_weight && weight_option == 'Ratio' && metric_percentages.length > 1" class="mt-4">
            <div v-for="(metric, i) in selected_metrics" :key="metric.id" class="flex mb-4">
              <div class="bg-[#B91979] p-2 rounded-full w-64 -mt-2 mr-4 truncate text-white flex space-x-2 items-center">
                {{ selected_metrics[i].name }}
                <X
                  class="w-6 h-6 ml-auto cursor-pointer"
                  color="white"
                  @click="selected_metrics = selected_metrics.filter((m) => m.id !== selected_metrics[i].id)" />
              </div>
              <div
                v-for="(weight, j) in Array(5)"
                :key="weight"
                :class="[`${j < metric_weights[i] ? 'bg-status-success-green' : 'bg-gray-300'} w-6 h-6 rounded-sm mx-0.5`]"
                @click="handleWeightClick(i, j)" />
              <div class="ml-2 text-lg dark:text-white">
                X{{
                  metric_weights[i] == 1
                    ? 0.25
                    : metric_weights[i] == 2
                      ? 0.5
                      : metric_weights[i] == 3
                        ? 1
                        : metric_weights[i] == 4
                          ? 1.5
                          : 2
                }}
              </div>
            </div>
          </div>

          <div
            v-if="metric_percentages.length > 1 && !equal_weight && weight_option == 'Percentage'"
            class="flex space-x-4 items-center w-full">
            <div class="mr-10">
              <div v-for="(metric_percentage, i) in metric_percentages" :key="i" class="mt-10 flex items-center space-x-5">
                <div class="bg-[#B91979] p-2 rounded-full w-64 truncate text-white flex space-x-2 items-center">
                  {{ selected_metrics[i].name }}
                  <X
                    class="w-6 h-6 ml-auto cursor-pointer"
                    color="white"
                    @click="selected_metrics = selected_metrics.filter((m) => m.id !== selected_metrics[i].id)" />
                </div>
                <div class="w-64 relative">
                  <Slider v-model="metric_percentages[i]" :tooltips="false" />
                  <div
                    :key="i"
                    :style="{
                      width: '60px',
                      marginLeft: `calc(${metric_percentages[i]}% - 40px)`
                    }"
                    :class="[`dark:text-white absolute  -mt-[62px] text-center`]">
                    <svg
                      width="71"
                      height="45"
                      viewBox="0 0 71 45"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="absolute z-10 scale-125">
                      <mask id="path-1-inside-1_3040_1994" fill="white">
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z" />
                      </mask>
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z"
                        fill="white" />
                      <path
                        d="M30.7239 37L32.4322 35.96L31.8478 35H30.7239V37ZM34.4946 43.1939L32.7863 44.2339L34.4946 43.1939ZM37.9113 43.1939L39.6196 44.2339L37.9113 43.1939ZM41.6821 37V35H40.5582L39.9738 35.96L41.6821 37ZM2 18.5C2 9.3873 9.3873 2 18.5 2V-2C7.17816 -2 -2 7.17816 -2 18.5H2ZM18.5 35C9.3873 35 2 27.6127 2 18.5H-2C-2 29.8218 7.17816 39 18.5 39V35ZM30.7239 35H18.5V39H30.7239V35ZM36.203 42.1539L32.4322 35.96L29.0155 38.04L32.7863 44.2339L36.203 42.1539ZM36.203 42.1539H36.203L32.7863 44.2339C34.3448 46.7939 38.0611 46.7939 39.6196 44.2339L36.203 42.1539ZM39.9738 35.96L36.203 42.1539L39.6196 44.2339L43.3904 38.04L39.9738 35.96ZM52.5 35H41.6821V39H52.5V35ZM69 18.5C69 27.6127 61.6127 35 52.5 35V39C63.8218 39 73 29.8218 73 18.5H69ZM52.5 2C61.6127 2 69 9.3873 69 18.5H73C73 7.17816 63.8218 -2 52.5 -2V2ZM18.5 2H52.5V-2H18.5V2Z"
                        fill="grey"
                        mask="url(#path-1-inside-1_3040_1994)" />
                    </svg>

                    <div class="flex group relative z-20">
                      <input
                        :value="metric_percentages[i]"
                        name="percentage"
                        type="number"
                        class="appearance-none focus:ring-0 block w-[76px] text-center ml-0 p-2 border-0 bg-transparent border-light-divider-blue dark:border-dark-divider-border rounded-md dark:bg-dark-screen-background text-grey-light dark:text-white text-[15px] focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue sm:text-sm"
                        @input="(e) => handlePercentageInput(e, i)">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="metric_percentages.length > 1" class="relative">
              <svg class="w-32 h-32">
                <circle
                  class="progress-value"
                  :class="[
                    { 'stroke-red-500': dynamic_value > 100 },
                    { 'stroke-green-500': dynamic_value == 100 },
                    ,
                    { 'stroke-[#2DA7D7]': dynamic_value < 100 }
                  ]"
                  :cx="radius"
                  :cy="radius"
                  :r="radius - stroke"
                  :stroke-dasharray="circumference"
                  :stroke-dashoffset="offset" />
              </svg>
              <div class="absolute inset-0 flex items-center justify-center text-xl mt-5 -ml-6">
                {{ dynamic_value }}/100
              </div>
              <div v-if="dynamic_value !== 100" class="mt-2 text-common-grey absolute w-44 text-center text-xs -ml-6">
                {{ $t("Please adjust value to be 100") }}
              </div>
            </div>
          </div>

          <div class="absolute bottom-10 w-[95%] flex justify-between">
            <div type="button" class="cursor-pointer text-blue w-fit m-0 ml-3" @click="backClick(2)">
              {{ $t("Previous Step") }}
            </div>
            <button
              type="button"
              class="py-3 -mt-4"
              :disabled="!selected_metrics.length || dynamic_value < 99 || dynamic_value > 101"
              :class="[
                `primary-btn w-fit ml-3  ${
                  !selected_metrics.length || dynamic_value < 99 || dynamic_value > 101 ? 'bg-gray-400 dark:bg-gray-400' : ''
                }`
              ]"
              @click="nextClick(2)">
              <ArrowDownToDot
                class="w-6 mr-2 absolute -ml-12 text-white"
                :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-90' : '-rotate-90']" />
              <span class="ml-10">{{ $t("Next Step") }}</span>
            </button>
            <!-- <button v-if="score_edit" type="button" class="primary-btn w-[140px] ml-3" @click="submitClick">Submit changes</button> -->
          </div>
        </div>

        <div v-else-if="steps[3].status == 'current'">
          <div class="mt-8 mb-10 text-2xl dark:text-white">
            {{ $t("Score Levels") }}
          </div>

          <div>
            <button class="btn primary-outline-btn w-fit" @click="handleAddLevel">
              <Plus class="w-5 h-5 mr-2" /> {{ $t("Add Level") }}
            </button>
            <button v-if="metric_levels > 2" class="btn primary-outline-btn w-fit" @click="handleRemoveLevel">
              <Minus class="w-5 h-5 mr-2" />{{ $t("Remove Level") }}
            </button>
          </div>

          <div class="ml-20">
            <div v-for="(metric, metric_index) in selected_metrics" :key="metric" class="px-4 my-44">
              <div class="relative bottom-20 font-medium">
                {{ metric.name }}:
              </div>

              <div v-if="metric.data_type !== 'Number'" class="grid grid-cols-1 -ml-10 gap-y-6">
                <div
                  v-for="(value, value_index) in metric_level_values[metric_index].values"
                  :key="value_index"
                  class="mr-8 dark:text-white">
                  <div class="mb-2">
                    {{ $t("Level") }} {{ value_index + 1 }}:
                  </div>
                  <div class="flex">
                    <Listbox
                      :value="metric_level_values[metric_index].operators[value_index]"
                      as="div"
                      class="relative mr-2 w-full h-[50px]">
                      <ListboxButton
                        class="relative z-10 w-full h-full overflow-hidden text-center bg-white border border-gray-300 rounded-md cursor-default dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border focus:outline-none">
                        <span v-if="metric_level_values[metric_index].operators[value_index]" class="block py-1 truncate text-thin">{{
                          metric_level_values[metric_index].operators[value_index].label
                        }}</span>
                        <span v-else class="block py-1 truncate text-thin">{{ $t("Select operator") }}</span>
                        <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronDown
                            v-if="!metric_level_values[metric_index].operators[value_index]"
                            class="w-5 h-5 text-gray-400"
                            aria-hidden="true" />
                        </span>
                      </ListboxButton>

                      <transition
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0">
                        <ListboxOptions
                          class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <ListboxOption
                            v-for="operator in metric.data_type == 'Date'
                              ? operators_for_dates
                              : metric.data_type == 'String'
                                ? operators_for_strings
                                : operators_for_strings"
                            :key="operator.value"
                            v-slot="{ active, selected }"
                            as="template"
                            :value="operator"
                            @click="handleOperatorSelect(metric_index, value_index, operator)">
                            <li
                              :class="[
                                active ? 'text-white bg-blue' : 'text-gray-900',
                                'cursor-default relative py-2 pl-5 pr-4 text-center has-tooltip'
                              ]">
                              <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                {{ operator.label }}
                              </span>

                              <span
                                v-if="selected"
                                :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                                <Check class="w-5 h-5" aria-hidden="true" />
                              </span>
                              <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                                {{ operator.label }}
                              </div>
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </Listbox>
                    <select
                      v-if="metric_level_values[metric_index].operators[value_index]?.value == 'blank'"
                      v-model="metric_level_values[metric_index].values[value_index]"
                      :class="['bg-light-screen-backtop-3 left-[89%]round dark:bg-dark-screen-background']"
                      class="w-[186px] mx-1 mr-2 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue appearance-none text-left block px-3 py-2.5 text-common-grey border border-light-divider-border dark:border-dark-divider-border rounded-lg shadow-sm"
                      @change="(e) => handleCheckAllValuesFilled(e)">
                      <option
                        :value="'True'"
                        selected
                        :class="['text-gray-900 dark:text-white appearance-none relative cursor-default py-2 pl-8 pr-4']">
                        {{ $t("True") }}
                      </option>
                      <option
                        :value="'False'"
                        :class="['text-gray-900 dark:text-white appearance-none relative cursor-default py-2 pl-8 pr-4']">
                        {{ $t("False") }}
                      </option>
                    </select>
                    <input
                      v-else
                      v-model="metric_level_values[metric_index].values[value_index]"
                      :type="
                        metric.data_type === 'Date' && metric_level_values[metric_index].operators[value_index]?.value?.includes('__')
                          ? 'number'
                          : metric.data_type === 'Date'
                            ? 'date'
                            : 'text'
                      "
                      class="z-50 appearance-none block w-full p-3 border border-light-divider-blue dark:border-dark-divider-border rounded-md dark:bg-dark-screen-background text-grey-light dark:text-white text-[15px] focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue sm:text-sm"
                      @input="handleCheckAllValuesFilled">
                  </div>
                </div>
              </div>
              <div
                v-if="metric.data_type === 'Number' && parseInt(metric_levels) > 2"
                class="rounded-full bg-white border shadow-md w-5 h-5 relative top-8 right-2 z-10" />
              <div v-if="parseInt(metric_levels) == 2 && metric.data_type === 'Number'" class="flex mt-4 dark:text-white">
                <div class="text-center">
                  <div class="text-xl">
                    {{ $t("Level 1") }}:
                  </div>
                  <div class="mt-4">
                    {{
                      metric_level_values[metric_index].operators[0] == "<"
                        ? $t("more than")
                        : metric_level_values[metric_index].operators[0] == ">"
                          ? $t("less than")
                          : metric_level_values[metric_index].operators[0] == ">="
                            ? $t("less than or equal to")
                            : $t("more than or equal to")
                    }}

                    <input
                      :value="metric_level_values[metric_index].max_value"
                      class="border rounded-md w-20 text-center -mt-2 relative z-10 bg-transparent ring-0 focus:ring-0"
                      type="number"
                      @input="(e) => handleInput(e, metric_level_values[metric_index], 'max_value')">
                  </div>
                </div>
                <div class="ml-10 text-center">
                  <div class="text-xl">
                    {{ $t("Level 2") }}:
                  </div>
                  <div class="mt-4">
                    {{
                      metric_level_values[metric_index].operators[0] == "<"
                        ? $t("less than")
                        : metric_level_values[metric_index].operators[0] == ">"
                          ? $t("more than")
                          : metric_level_values[metric_index].operators[0] == ">="
                            ? $t("more than or equal to")
                            : $t("less than or equal to")
                    }}
                    <input
                      :value="metric_level_values[metric_index].max_value"
                      class="border rounded-md w-20 text-center -mt-2 relative z-10 bg-transparent ring-0 focus:ring-0"
                      type="number"
                      @input="(e) => handleInput(e, metric_level_values[metric_index], 'max_value')">
                  </div>
                </div>
              </div>

              <div class="w-full">
                <div
                  v-if="metric.data_type === 'Number' && parseInt(metric_levels) > 2"
                  class="absolute -ml-10 -mt-9 p-0.5 px-2 rounded-md">
                  <svg
                    width="71"
                    height="45"
                    viewBox="0 0 71 45"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="absolute scale-125 -mt-2">
                    <mask id="path-1-inside-1_3040_1994" fill="white">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z" />
                    </mask>
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z"
                      fill="white" />
                    <path
                      d="M30.7239 37L32.4322 35.96L31.8478 35H30.7239V37ZM34.4946 43.1939L32.7863 44.2339L34.4946 43.1939ZM37.9113 43.1939L39.6196 44.2339L37.9113 43.1939ZM41.6821 37V35H40.5582L39.9738 35.96L41.6821 37ZM2 18.5C2 9.3873 9.3873 2 18.5 2V-2C7.17816 -2 -2 7.17816 -2 18.5H2ZM18.5 35C9.3873 35 2 27.6127 2 18.5H-2C-2 29.8218 7.17816 39 18.5 39V35ZM30.7239 35H18.5V39H30.7239V35ZM36.203 42.1539L32.4322 35.96L29.0155 38.04L32.7863 44.2339L36.203 42.1539ZM36.203 42.1539H36.203L32.7863 44.2339C34.3448 46.7939 38.0611 46.7939 39.6196 44.2339L36.203 42.1539ZM39.9738 35.96L36.203 42.1539L39.6196 44.2339L43.3904 38.04L39.9738 35.96ZM52.5 35H41.6821V39H52.5V35ZM69 18.5C69 27.6127 61.6127 35 52.5 35V39C63.8218 39 73 29.8218 73 18.5H69ZM52.5 2C61.6127 2 69 9.3873 69 18.5H73C73 7.17816 63.8218 -2 52.5 -2V2ZM18.5 2H52.5V-2H18.5V2Z"
                      fill="gray"
                      mask="url(#path-1-inside-1_3040_1994)" />
                  </svg>

                  <input
                    class="border-0 w-20 relative -ml-2 -mt-2 z-10 bg-transparent text-center ring-0 focus:ring-0"
                    type="number"
                    :value="metric_level_values[metric_index].min_value"
                    @input="(e) => handleInput(e, metric_level_values[metric_index], 'min_value')">
                </div>
              </div>
              <div
                v-if="metric.data_type === 'Number' && parseInt(metric_levels) > 2"
                class="rounded-full bg-white w-5 h-5 relative border shadow-md top-3 left-[89%] z-10" />

              <div
                v-if="metric.data_type === 'Number' && parseInt(metric_levels) > 2"
                class="absolute z-10 left-[calc(89%-58px)] -mt-14 p-0.5 px-2 rounded-md text-sm">
                <svg
                  width="71"
                  height="45"
                  viewBox="0 0 71 45"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="absolute scale-125 -mt-2 ml-1">
                  <mask id="path-1-inside-1_3040_1994" fill="white">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z" />
                  </mask>
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z"
                    fill="white" />
                  <path
                    d="M30.7239 37L32.4322 35.96L31.8478 35H30.7239V37ZM34.4946 43.1939L32.7863 44.2339L34.4946 43.1939ZM37.9113 43.1939L39.6196 44.2339L37.9113 43.1939ZM41.6821 37V35H40.5582L39.9738 35.96L41.6821 37ZM2 18.5C2 9.3873 9.3873 2 18.5 2V-2C7.17816 -2 -2 7.17816 -2 18.5H2ZM18.5 35C9.3873 35 2 27.6127 2 18.5H-2C-2 29.8218 7.17816 39 18.5 39V35ZM30.7239 35H18.5V39H30.7239V35ZM36.203 42.1539L32.4322 35.96L29.0155 38.04L32.7863 44.2339L36.203 42.1539ZM36.203 42.1539H36.203L32.7863 44.2339C34.3448 46.7939 38.0611 46.7939 39.6196 44.2339L36.203 42.1539ZM39.9738 35.96L36.203 42.1539L39.6196 44.2339L43.3904 38.04L39.9738 35.96ZM52.5 35H41.6821V39H52.5V35ZM69 18.5C69 27.6127 61.6127 35 52.5 35V39C63.8218 39 73 29.8218 73 18.5H69ZM52.5 2C61.6127 2 69 9.3873 69 18.5H73C73 7.17816 63.8218 -2 52.5 -2V2ZM18.5 2H52.5V-2H18.5V2Z"
                    fill="gray"
                    mask="url(#path-1-inside-1_3040_1994)" />
                </svg>

                <input
                  :value="metric_level_values[metric_index].max_value"
                  class="border-0 w-20 text-center -mt-2 relative z-10 bg-transparent ring-0 focus:ring-0"
                  type="number"
                  @input="(e) => handleInput(e, metric_level_values[metric_index], 'max_value')">
              </div>

              <div v-if="metric.data_type === 'Number' && parseInt(metric_levels) > 3" class="relative origin-top">
                <Slider
                  :value="metric_level_values[metric_index].plotted_values"
                  :tooltips="false"
                  class="w-[100%]"
                  :min="
                    parseInt(
                      metric_level_values[metric_index].reversed
                        ? metric_level_values[metric_index].min_value * -1
                        : metric_level_values[metric_index].min_value
                    )
                  "
                  :max="
                    parseInt(
                      metric_level_values[metric_index].reversed
                        ? metric_level_values[metric_index].max_value * -1
                        : metric_level_values[metric_index].max_value
                    )
                  "
                  :options="{ connect: metric_level_values[metric_index].connect }"
                  @change="(arr) => handleMetricValues(arr, metric_index, metric_level_values[metric_index].reversed)" />

                <div
                  v-for="(metric_level, metric_level_index) in metric_level_values[metric_index].plotted_values"
                  :key="metric_level_index"
                  class="absolute z-10 -mt-14"
                  :style="{
                    marginLeft: `calc(${
                      ((metric_level - metric_level_values[metric_index].min_value) /
                        (metric_level_values[metric_index].max_value - metric_level_values[metric_index].min_value)) *
                      100
                    }% - 50px)`
                  }">
                  <svg
                    width="71"
                    height="45"
                    viewBox="0 0 71 45"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="absolute scale-125 -mt-2 ml-3">
                    <mask id="path-1-inside-1_3040_1994" fill="white">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z" />
                    </mask>
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z"
                      fill="white" />
                    <path
                      d="M30.7239 37L32.4322 35.96L31.8478 35H30.7239V37ZM34.4946 43.1939L32.7863 44.2339L34.4946 43.1939ZM37.9113 43.1939L39.6196 44.2339L37.9113 43.1939ZM41.6821 37V35H40.5582L39.9738 35.96L41.6821 37ZM2 18.5C2 9.3873 9.3873 2 18.5 2V-2C7.17816 -2 -2 7.17816 -2 18.5H2ZM18.5 35C9.3873 35 2 27.6127 2 18.5H-2C-2 29.8218 7.17816 39 18.5 39V35ZM30.7239 35H18.5V39H30.7239V35ZM36.203 42.1539L32.4322 35.96L29.0155 38.04L32.7863 44.2339L36.203 42.1539ZM36.203 42.1539H36.203L32.7863 44.2339C34.3448 46.7939 38.0611 46.7939 39.6196 44.2339L36.203 42.1539ZM39.9738 35.96L36.203 42.1539L39.6196 44.2339L43.3904 38.04L39.9738 35.96ZM52.5 35H41.6821V39H52.5V35ZM69 18.5C69 27.6127 61.6127 35 52.5 35V39C63.8218 39 73 29.8218 73 18.5H69ZM52.5 2C61.6127 2 69 9.3873 69 18.5H73C73 7.17816 63.8218 -2 52.5 -2V2ZM18.5 2H52.5V-2H18.5V2Z"
                      fill="gray"
                      mask="url(#path-1-inside-1_3040_1994)" />
                  </svg>

                  <input
                    :value="metric_level_values[metric_index].plotted_values[metric_level_index]"
                    class="border-0 w-20 bg-transparent text-center relative z-10 -mt-2 ml-1 ring-0 focus:ring-0"
                    type="number"
                    @input="
                      (e) =>
                        handleInput(
                          e,
                          metric_level_values[metric_index].plotted_values,
                          metric_level_index,
                          metric_level_values[metric_index].values,
                          metric_level_index + 1
                        )
                    ">
                </div>
              </div>

              <div v-if="metric.data_type === 'Number' && parseInt(metric_levels) > 2" class="relative flex mt-3 dark:text-white">
                <div class="absolute -ml-24 text-center">
                  <div>{{ $t("Level 1") }}</div>
                  <div>
                    {{
                      `${
                        metric_level_values[metric_index].operators[0] == "<"
                          ? $t("more than")
                          : metric_level_values[metric_index].operators[0] == ">"
                            ? $t("less than")
                            : metric_level_values[metric_index].operators[0] == ">="
                              ? $t("less than or equal to")
                              : $t("more than or equal to")
                      } ${$utils.truncateNumber(metric_level_values[metric_index].min_value)}`
                    }}
                  </div>
                </div>
                <div v-if="parseInt(metric_levels) == 3" class="absolute w-[90%] h-3 bg-gray-200 border rounded-full -mt-4" />

                <div class="absolute text-center right-[1%]">
                  <div>{{ $t("Level") }} {{ metric_levels }}</div>
                  <div>
                    {{
                      `${
                        metric_level_values[metric_index].operators[0] == "<"
                          ? $t("less than")
                          : metric_level_values[metric_index].operators[0] == ">"
                            ? $t("more than")
                            : metric_level_values[metric_index].operators[0] == ">="
                              ? $t("more than or equal to")
                              : $t("less than or equal to")
                      } 
                      ${$utils.truncateNumber(metric_level_values[metric_index].max_value)}`
                    }}
                  </div>
                </div>

                <div
                  v-for="(value, j) in metric_level_values[metric_index].values.slice(
                    0,
                    metric_level_values[metric_index].values.length - 1
                  )"
                  :key="value.id"
                  :style="{
                    width: `${
                      90 *
                      ((metric_level_values[metric_index].values[j + 1] - metric_level_values[metric_index].values[j]) /
                        (parseInt(metric_level_values[metric_index].max_value) - parseInt(metric_level_values[metric_index].min_value)))
                    }%`
                  }"
                  :class="[` text-center`]">
                  <div>{{ $t("Level") }} {{ j + 2 }}</div>
                  <!-- <div>
                    {{
                      !j
                        ? `from ${$utils.truncateNumber(metric_level_values[metric_index].min_value)} to ${$utils.truncateNumber(
                          metric_level_values[metric_index].values[1]
                        )}`
                        : `from ${$utils.truncateNumber(metric_level_values[metric_index].values[j])} to ${$utils.truncateNumber(
                          metric_level_values[metric_index].values[j + 1]
                        )}`
                    }}
                  </div> -->
                  <!-- <div>{{ metric_percentages[metric_index] }}%</div> -->
                </div>
              </div>

              <div class="border-b relative top-12 w-[90%] dark:border-dark-divider-border h-4" />

              <!-- <div class="flex -ml-20">
            <div>{{ ascending_levels ? "Level 1" : `Level ${metric_levels}` }}</div>
            <div>{{ ascending_levels ? `Level ${metric_levels}` : "Level 1" }}</div>
          </div> -->

              <!-- <div>
            <div class="absolute -mt-5">
              <div v-for="(metric_level_value, j) in metric_level_values[metric_index].values" :key="metric_level_value" class="my-20">
                <div v-if="!j || (j + 1 == metric_level_values[metric_index].values.length && metric.data_type === 'Number')" :class="[`relative ${!j ? 'bottom-3' : 'top-3'} `]">
                  {{ metric_level_values[metric_index].reversed && !j ? "Less than" : !metric_level_values[metric_index].reversed && !j ? "Greater than" : !metric_level_values[metric_index].reversed && j + 1 === metric_level_values[metric_index].values.length ? "Less than" : metric_level_values[metric_index].reversed && j + 1 === metric_level_values[metric_index].values.length ? "Greater than" : "" }} {{ metric_level_value }}
                </div>

                <div v-else>
                  <div :class="[` ${!metric_level_values[metric_index].values[j - 1] ? 'opacity-0' : ''} absolute -mt-12 w-[140px]`]">
                    {{ metric_level_values[metric_index].reversed ? `Greater than ${metric_level_values[metric_index].values[j - 1]} and less than:` : `Less than ${metric_level_values[metric_index].values[j - 1]} and greater than:` }}
                  </div>
                  <input
                    :value="metric_level_values[metric_index].values[j]"
                    name="weight"
                    type="number"
                    class="appearance-none block w-[120px] p-2 border border-light-divider-blue dark:border-dark-divider-border rounded-md dark:bg-dark-screen-background text-grey-light dark:text-white text-[15px] focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue sm:text-sm"
                    @input="(e) => handleMetricLevelValueChange(e, metric_index, j)"
                  />
                </div>
              </div>
            </div>
          </div> -->
            </div>
          </div>

          <div v-if="overlapping_values.length" class="mb-2 font-bold text-red-400">
            {{ $t("Overlapping values needs to be fixed before proceeding") }}:
          </div>
          <div v-for="(overlapping_value, i) in overlapping_values" :key="i">
            <div class="text-red-400">
              &#8226; {{ overlapping_value }}
            </div>
          </div>

          <div class="absolute bottom-10 w-[95%] flex justify-between">
            <div type="button" class="cursor-pointer text-blue w-fit m-0 ml-3" @click="backClick(3)">
              {{ $t("Previous Step") }}
            </div>
            <button
              type="button"
              class="py-3 -mt-4"
              :disabled="!all_metric_values_completed || !selected_metrics.length || dynamic_value < 99 || dynamic_value > 101"
              :class="[
                `primary-btn w-fit ml-3  ${
                  !all_metric_values_completed || !selected_metrics.length || dynamic_value < 99 || dynamic_value > 101
                    ? 'bg-gray-400 dark:bg-gray-400'
                    : ''
                }`
              ]"
              @click="nextClick(3)">
              <ArrowDownToDot
                class="w-6 mr-2 absolute -ml-12 text-white"
                :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-90' : '-rotate-90']" />
              <span class="ml-10">{{ $t("Next Step") }} </span>
            </button>
            <!-- <button v-if="score_edit" type="button" class="primary-btn w-[140px] ml-3" @click="submitClick">Submit changes</button> -->
          </div>
        </div>

        <div v-else-if="steps[4].status == 'current'">
          <div class="mt-8 mb-10 text-2xl dark:text-white">
            {{ $t("Color Options") }}
          </div>

          <Colors
            :metric-levels="metric_levels"
            :is-score="true"
            :metric="score"
            class="p-2 mt-2"
            @update="
              ($event) => {
                score.metric_colors = $event.metric_colors
              }
            " />

          <div class="absolute bottom-10 w-[95%] flex justify-between">
            <div type="button" class="cursor-pointer text-blue w-fit m-0 ml-3" @click="backClick(4)">
              {{ $t("Previous Step") }}
            </div>
            <button
              type="button"
              :disabled="!all_values_filled"
              class="py-3 -mt-4"
              :class="[`primary-btn w-fit ml-3  ${!all_values_filled ? 'bg-gray-400 dark:bg-gray-400' : ''}`]"
              @click="constructFormula">
              <GitBranchPlus class="w-6 mr-2 absolute -ml-16" color="white" /> <span class="ml-10">{{ $t("Create Score") }}</span>
            </button>
          </div>
        </div>

        <div v-else-if="steps[5].status == 'current'">
          <div class="flex-row justify-center w-full mt-8 text-center align-middle">
            <CircleCheck class="ml-[46%] h-20 w-20 text-green-400 mb-4" />
            <div class="text-2xl text-common-grey">
              {{ $t("Score") }} {{ score_edit ? $t("modified") : $t("created") }} {{ $t("successfully") }}
            </div>
            <button type="button" class="cancel-btn w-[160px] m-0" @click="$router.replace(`/admin/metrics?tab=scores`)">
              {{ $t("Go to Scores") }}
            </button>
            <button type="button" :class="[`primary-btn w-[200px] ml-3 mt-10 `]" @click="$router.replace(`/admin/scores/new-score`)">
              {{ $t("Create New Score") }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="steps[0].status !== 'current'"
        class="bg-[#2DA7D710] w-[480px] h-full border-dashed border border-[#2DA7D7] py-4 px-2 flex flex-col items-center">
        <div class="text-sm text-common-grey">
          {{ $t("You are creating a score to be used") }} <br>
          {{ $t("in other formulas, tables and as KPI's") }}
        </div>

        <div class="flex space-x-2 text-sm text-common-grey mt-10">
          <span class="whitespace-nowrap">{{ $t("Select any") }}</span>
          <span class="p-0.5 mr-2 w-4 h-4 bg-[#899399] flex items-center justify-center rounded-full text-white ml-2 mt-0.5 has-tooltip">
            {{ $t("?") }}
          </span>
          <span class="ml-2">{{ $t("to get additional information about it.") }}</span>
        </div>

        <div :class="[{ '!h-[120px]': steps[4].status !== 'current' }]" class="w-[400px] h-full mt-10">
          <div v-if="steps[4].status == 'current'" class="h-[300px] bg-white border rounded-md p-2">
            <div class="flex space-x-4 w-full mb-2">
              <div class="w-[40%] ml-[20%]">
                {{ $t("Name") }}
              </div>
              <div class="w-[40%]">
                {{ score_name }}
              </div>
            </div>

            <div
              v-for="(range, range_index) in score?.metric_colors.ranges"
              :key="range_index"
              :class="{ 'bg-gray-50 pt-0.5': range_index % 2 }"
              class="flex w-full mt-1 text-common-grey text-sm">
              <div class="w-[20%]">
                #{{ range_index + 1 }}
              </div>
              <div class="w-[40%]">
                {{ names_list[range_index] }}
              </div>
              <div
                :style="{
                  color:
                    score?.metric_colors.metric_color_display_option == 'text' ||
                    score?.metric_colors.metric_color_display_option == 'mixed'
                      ? range[0]
                      : null,
                  background:
                    score?.metric_colors.metric_color_display_option == 'background' ||
                    score?.metric_colors.metric_color_display_option == 'mixed'
                      ? `${range[0]}${score?.metric_colors.metric_color_display_opacity}`
                      : null
                }"
                class="w-[40%] p-1 flex items-center">
                <div
                  v-if="
                    score?.metric_colors.metric_color_display_option == 'dot' || score?.metric_colors.metric_color_display_option == 'mixed'
                  "
                  class="w-3 h-3 mr-1 rounded-full"
                  :style="{ background: range ? range[0] : null }" />
                {{ range[1] }}
              </div>
            </div>
          </div>

          <div v-if="steps[0].status !== 'current' && !aggregation_method" class="mt-2">
            {{ $t("A KPI is used to show results for ONE record at a time.") }}
          </div>

          <div v-if="steps[0].status !== 'current' && aggregation_method" class="mt-2">
            {{ $t("A Group KPI is used to show results for MANY records or a GROUP of records at a time.") }}
          </div>

          <div v-if="steps[0].status !== 'current' && !aggregation_method" class="w-[320] bg-white h-fit border rounded-md p-2 mt-2">
            <div class="flex space-x-4 w-full mb-2">
              <div class="w-[40%] ml-[20%]">
                {{ $t("Name") }}
              </div>
              <div class="w-[40%]">
                {{ score_name }}
              </div>
            </div>

            <div
              v-for="(range, range_index) in [1, 2, 3, 4]"
              :key="range_index"
              :class="{ 'bg-gray-50 pt-0.5': range_index % 2 }"
              class="flex w-full mt-1 text-common-grey text-sm">
              <div class="w-[20%]">
                #{{ range_index + 1 }}
              </div>
              <div class="w-[40%]">
                {{ !range_index ? "William" : range_index == 1 ? "Adam" : range_index == 2 ? "Chirs" : "Steven" }}
              </div>
              <div class="w-[40%] p-1 flex items-center">
                <div class="w-3 h-3 mr-1 rounded-full" />
                {{ $t("Value") }}
              </div>
            </div>
          </div>

          <RollUpMetric
            v-else-if="steps[0].status !== 'current' && aggregation_method"
            :metric="{
              ...$store.state.metrics.filter((m) => m.data_type === 'Number')[0],
              value: 5,
              name: score_name,
              history: [
                {
                  datetime: '2022-12-01T00:00:00Z',
                  value: 3
                },
                {
                  datetime: '2022-12-01T00:00:00Z',
                  value: 8
                },
                {
                  datetime: '2023-01-01T00:00:00Z',
                  value: 5
                }
              ]
            }"
            :item-layout-width="12"
            :item-layout-height="6" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ArrowDownToDot, GitBranchPlus, ChevronDown, Check, Plus, CircleCheck, Minus, X } from "lucide-vue-next"
import RollUpMetric from "@/components/shared/rollup-metrics/RollUpMetric.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import Slider from "@vueform/slider"
import TextArea from "@/components/shared/TextArea.vue"
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue"
import dataService from "@/services/data.service"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import TextInput from "@/components/shared/TextInput.vue"
import Colors from "@/views/dashboard/admin/metrics/form-tabs/Colors.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import AutoCompleteInput from "@/components/shared/AutoCompleteInput.vue"
import { Switch } from "@headlessui/vue"

export default {
  components: {
    ArrowDownToDot,
    GitBranchPlus,
    RollUpMetric,
    Switch,
    Plus,
    Minus,
    TextInput,
    ComboBoxInput,
    CircleCheck,
    Check,
    X,
    AutoCompleteInput,
    SelectInput,
    Slider,
    TextArea,
    ChevronDown,
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    Colors
  },

  data() {
    return {
      percentage_input_timeout: null,
      input_timeout: null,
      score: {
        metric_colors: { ranges: [], is_primary_metric_color: false, metric_color_display_option: "dot", metric_color_display_opacity: 15 }
      },
      metrics_aggregation_methods: [
        { name: this.$t("SUM"), value: "sum" },
        { name: this.$t("MIN"), value: "min" },
        { name: this.$t("MAX"), value: "max" },
        { name: this.$t("COUNT"), value: "count" },
        { name: this.$t("AVERAGE"), value: "average" }
      ],
      aggregation_method: "",
      add_aggregation: false,
      scores_loaded: false,
      percentage_slider: { options: { connect: [false, false] } },
      prevent_watch: false,
      score_edit: false,
      operators_for_strings: [
        { label: this.$t("Equal to "), value: "equal" },
        { label: this.$t("Includes"), value: "includes" },
        { label: this.$t("Does not equal to"), value: "not_equal" },
        { label: this.$t("Does not include"), value: "not_includes" },
        { label: this.$t("Is Blank"), value: "blank" }
      ],
      operators_for_numbers: [
        { label: this.$t("Equal"), value: "==" },
        { label: this.$t("Greater than "), value: ">" },
        { label: this.$t("Greater than or equal to"), value: ">=" },
        { label: this.$t("Less than"), value: "<" },
        { label: this.$t("Less than or equal to"), value: "<=" }
      ],
      operators_for_dates: [
        { label: this.$t("After"), value: "after" },
        { label: this.$t("Before"), value: "before" },
        { label: this.$t("Exact Date"), value: "exact" },
        { label: this.$t("More than days ago"), value: "__DAYS_WITHIN_LAST_X_DAYS__" },
        { label: this.$t("Less than days ago"), value: "__DAYS_MORE_THAN_X_DAYS_AGO__" },
        { label: this.$t("Exact days ago"), value: "__DAYS_EXACT_DAYS_AGO__" },
        { label: this.$t("More than days from now"), value: "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" },
        { label: this.$t("Less than days from now"), value: "__DAYS_WITHIN_NEXT_X_DAYS__" },
        { label: this.$t("Exact days from now"), value: "__DAYS_EXACT_DAYS_FROM_NOW__" },
        { label: this.$t("Is Blank"), value: "blank" }
      ],
      steps: [
        { name: this.$t("Details"), status: "current" },
        { name: this.$t("Display"), status: "" },
        { name: this.$t("Metric Selection"), status: "" },
        { name: this.$t("Score Levels"), status: "" },
        { name: this.$t("Color Options"), status: "" },
        { name: this.$t("Done"), status: "" }
      ],
      score_name: "",
      score_object_type: "company",
      score_description: "",
      selected_metrics: [],
      metric_weights: [],

      weight_options: ["Percentage", "Ratio"],
      weight_option: "Percentage",
      equal_weight: true,

      metric_levels: 3,

      ascending_levels: false,
      metric_level_values: [],
      slider_handle_postions: [],

      metric_percentages: [],
      percentages_margin_left_array: [],
      max_and_min_values_filled: false,
      all_values_filled: true,
      metrics_to_customize: [],
      overlapping_values: [],

      radius: 50,
      stroke: 5
    }
  },

  computed: {
    all_metric_values_completed() {
      let completed_calc = true
      this.metric_level_values.forEach((metric_level_value) => {
        if (metric_level_value.operators) {
          if (metric_level_value.operators.filter((o) => !o).length) {
            completed_calc = false
          }
        }

        if (metric_level_value.values) {
          if (metric_level_value.values.filter((v) => !v && v !== 0).length) {
            completed_calc = false
          }
        }
      })

      return completed_calc
    },

    names_list() {
      return ["William", "David", "Steven", "Peter", "Omar", "Adam", "Haydar", "Robert"]
    },
    dynamic_value() {
      let value_calc = 0
      this.metric_percentages.forEach((v) => {
        value_calc += v
      })
      return value_calc
    },
    circumference() {
      return 2 * Math.PI * (this.radius - this.stroke)
    },
    offset() {
      const progress = Math.min(Math.max(0, this.dynamic_value), 100)
      return this.circumference * (1 - progress / 100)
    },
    object_type_singular() {
      return this.$store.state.object_types.find(
        (o) =>
          (o.plural_label ? o.plural_label : this.$utils.capitalize(this.$utils.makePlural(o.type))) ==
          this.$route.query?.redirect_url?.split("/").slice(-1)
      )?.type
    }
  },

  watch: {
    metric_level_values: {
      deep: true,
      handler() {
        this.metric_level_values.forEach((m, i) => {
          if (m.operators[0] == "<" || m.operators[0] == "<=") {
            this.metric_level_values[i].reversed = true
          }
          if (m.operators[0] == "<") {
            this.metric_level_values[i].operators[this.metric_levels - 1] = ">"
          }
          if (m.operators[0] == ">") {
            this.metric_level_values[i].operators[this.metric_levels - 1] = "<"
          }
          if (m.operators[0] == "<=") {
            this.metric_level_values[i].operators[this.metric_levels - 1] = ">="
          }
          if (m.operators[0] == ">=") {
            this.metric_level_values[i].operators[this.metric_levels - 1] = "<="
          }
        })
      }
    },

    selected_metrics: {
      deep: true,
      handler() {
        if (!this.prevent_watch) {
          this.metric_weights = []
          this.metric_percentages = []
          this.percentage_slider.options.connect = [false]
          this.percentages_margin_left_array = [0]
          let margin_left_calc = 0
          this.slider_handle_postions = []
          const percentage_calc = Math.floor(100 / this.selected_metrics.length)
          this.all_values_filled = true
          this.metric_level_values = []
          this.selected_metrics.forEach((metric) => {
            if (metric.data_type !== "Number") {
              this.all_values_filled = false
            }
            margin_left_calc += percentage_calc
            this.percentages_margin_left_array.push(margin_left_calc)
            this.slider_handle_postions.push(margin_left_calc)
            this.metric_weights.push(3)
            this.metric_percentages.push(percentage_calc)
            this.percentage_slider.options.connect.push(false)
            this.metric_level_values.push({ reversed: false, operators: [], values: [] })
          })
          this.slider_handle_postions.splice(this.slider_handle_postions.length - 1, 1)
          this.percentage_slider.options.connect.splice(1, 1)
        }
      }
    }
  },

  created() {
    if (this.object_type_singular) {
      this.score_object_type = this.object_type_singular
    }

    if (this.$route.params.sid) {
      this.steps[1].status = "complete"
      this.steps[2].status = "complete"
      this.steps[3].status = "complete"
      this.steps[4].status = "complete"
      this.steps[5].status = "complete"

      dataService.getOrganizationMetric(this.$route.params.sid).then((res) => {
        //GET

        this.score_edit = true
        const score = res.data

        this.score = score
        if (score.aggregation_method) {
          this.aggregation_method = score.aggregation_method
          this.add_aggregation = true
        }

        this.score_name = score.name
        this.score_object_type = score.object_type
        this.score_description = score.description
        this.ascending_levels = score.is_score_logic_reversed

        const formula = score.formulary.expression.slice(14, score.formulary.expression.length - 1)
        let open_brackets = 0
        let end_position = null

        formula.split("").forEach((char, i) => {
          if (char == "[") {
            open_brackets += 1
          }
          if (char == "]") {
            open_brackets -= 1
          }

          if (!open_brackets && !end_position) {
            end_position = i
          }
        })

        const cases_array = JSON.parse(
          formula
            .slice(0, end_position + 1)
            .replace(/type:/g, '"type":')
            .replace(/default:/g, '"defautlt":')
            .replace(/'cases'/g, '"cases"')
            .replace(/'0'/g, '"0"')
            .replace(/cases:/g, '"cases":')
            .replace(/ == /g, "=")
            .replace(/ != /g, "^")
            .replace(/ in /g, "%")
            .replace(/ < /g, "$")
            .replace(/ > /g, "@")
            .replace(/<=/g, "~")
            .replace(/>=/g, "#")
        )

        this.metric_percentages = JSON.parse(formula.slice(end_position + 2, formula.length))
        const first_value = JSON.parse(formula.slice(end_position + 2, formula.length))[0]
        let total = 0
        this.equal_weight = true
        JSON.parse(formula.slice(end_position + 2, formula.length)).forEach((percentage) => {
          if (percentage !== first_value) {
            this.equal_weight = false
          }
          total += parseInt(percentage)
        })

        this.percentage_slider.options.connect = []
        if (total <= 101 && total >= 99) {
          this.weight_option = "Percentage"

          let margin_left_calc = 0
          this.percentages_margin_left_array = [0]
          this.metric_percentages.forEach((v, i) => {
            this.metric_weights.push(3)
            margin_left_calc += parseInt(v)
            this.percentages_margin_left_array.push(margin_left_calc)
            if (this.metric_percentages.length - 1 !== i) {
              this.slider_handle_postions.push(margin_left_calc)
            }
            this.percentage_slider.options.connect.push(false)
          })
        } else {
          this.weight_option = "Ratio"
          this.metric_weights = []
          this.metric_percentages.forEach((v) => {
            if (v == 0.25) {
              this.metric_weights.push(1)
            }
            if (v == 0.5) {
              this.metric_weights.push(2)
            }
            if (v == 1) {
              this.metric_weights.push(3)
            }
            if (v == 1.5) {
              this.metric_weights.push(4)
            }
            if (v == 2) {
              this.metric_weights.push(5)
            }
          })
        }

        cases_array.forEach((case_obj, case_index) => {
          let should_customize = false
          this.metric_levels = case_obj.cases.length
          const first_when = case_obj.cases[0].when
          let metric_name = ""
          let found_metric = false
          let metric_name_end_pos = null
          first_when.split("").forEach((c, i) => {
            if (this.$utils.getMetricByReferenceName(metric_name) && !found_metric) {
              metric_name_end_pos = i
              found_metric = true

              // this.prevent_watch = true

              this.chooseMetric(this.$utils.getMetricByReferenceName(metric_name))
              // this.selected_metrics.push(this.$utils.getMetricByReferenceName(metric_name))

              setTimeout(function () {
                this.prevent_watch = false
              }, 500)
            } else if (!found_metric) {
              metric_name += c
            }
          })

          this.metric_level_values.push({ values: [], operators: [], plotted_values: [], reversed: false, connect: [] })
          case_obj.cases.forEach((metric_case_obj, i) => {
            const operator = metric_case_obj.when.split("").slice(metric_name_end_pos, metric_name_end_pos + 1)[0]
            const value_after_operator = metric_case_obj.when.split(operator)[1]

            if (!i) {
              if (operator == ">" || operator == "#") {
                this.metric_level_values[this.metric_level_values.length - 1].reversed = true
              }
              if (isNaN(parseInt(value_after_operator))) {
                if (value_after_operator.split(`'`).length > 1) {
                  this.metric_level_values[this.metric_level_values.length - 1].min_value = value_after_operator.split(`'`)[1]
                  this.metric_level_values[this.metric_level_values.length - 1].values.push(value_after_operator.split(`'`)[1])
                  this.metric_level_values[this.metric_level_values.length - 1].operators.push(this.getOperator(operator))
                } else if (value_after_operator == "None") {
                  this.metric_level_values[this.metric_level_values.length - 1].min_value = operator == "^" ? "False" : "True"
                  this.metric_level_values[this.metric_level_values.length - 1].values.push(operator == "^" ? "False" : "True")
                  this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                    this.operators_for_dates.find((o) => o.value === "blank")
                  )
                } else {
                  if (value_after_operator.includes("today")) {
                    this.metric_level_values[this.metric_level_values.length - 1].min_value = value_after_operator
                      .split(`timedelta(`)[1]
                      .split(")")[0]
                    this.metric_level_values[this.metric_level_values.length - 1].values.push(
                      value_after_operator.split(`timedelta(`)[1].split(")")[0]
                    )
                    this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                      this.getOperator(operator, value_after_operator)
                    )
                  } else {
                    this.metric_level_values[this.metric_level_values.length - 1].min_value = value_after_operator
                    this.metric_level_values[this.metric_level_values.length - 1].values.push(value_after_operator)
                    this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                      this.getOperator(operator, value_after_operator)
                    )
                  }
                }
              } else {
                this.metric_level_values[this.metric_level_values.length - 1].min_value = parseInt(value_after_operator)
                this.metric_level_values[this.metric_level_values.length - 1].values.push(parseInt(value_after_operator))
                this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                  this.getOperator(operator, value_after_operator, true)
                )

                if (operator == "=") {
                  if (!this.metrics_to_customize.includes(this.$utils.getMetricByReferenceName(metric_name).id)) {
                    should_customize = true
                  }
                }
              }
            } else if (i + 1 == this.metric_levels) {
              if (isNaN(parseInt(value_after_operator))) {
                if (value_after_operator.split(`'`).length > 1) {
                  this.metric_level_values[this.metric_level_values.length - 1].max_value = value_after_operator.split(`'`)[1]
                  this.metric_level_values[this.metric_level_values.length - 1].values.push(value_after_operator.split(`'`)[1])
                  this.metric_level_values[this.metric_level_values.length - 1].operators.push(this.getOperator(operator))
                } else if (value_after_operator == "None") {
                  this.metric_level_values[this.metric_level_values.length - 1].max_value = operator == "^" ? "False" : "True"
                  this.metric_level_values[this.metric_level_values.length - 1].values.push(operator == "^" ? "False" : "True")
                  this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                    this.operators_for_dates.find((o) => o.value === "blank")
                  )
                } else {
                  if (value_after_operator.includes("today")) {
                    this.metric_level_values[this.metric_level_values.length - 1].max_value = value_after_operator
                      .split(`timedelta(`)[1]
                      .split(")")[0]
                    this.metric_level_values[this.metric_level_values.length - 1].values.push(
                      value_after_operator.split(`timedelta(`)[1].split(")")[0]
                    )
                    this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                      this.getOperator(operator, value_after_operator)
                    )
                  } else {
                    this.metric_level_values[this.metric_level_values.length - 1].max_value = value_after_operator
                    this.metric_level_values[this.metric_level_values.length - 1].values.push(value_after_operator)
                    this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                      this.getOperator(operator, value_after_operator)
                    )
                  }
                }
              } else {
                if (operator == "=") {
                  if (!this.metrics_to_customize.includes(this.$utils.getMetricByReferenceName(metric_name).id)) {
                    should_customize = true
                  }
                }
                this.metric_level_values[this.metric_level_values.length - 1].max_value = parseInt(value_after_operator)
                this.metric_level_values[this.metric_level_values.length - 1].values.push(parseInt(value_after_operator))
              }
            } else {
              if (isNaN(parseInt(value_after_operator))) {
                if (value_after_operator.split(`'`).length > 1) {
                  // this.metric_level_values[this.metric_level_values.length - 1].max_value = value_after_operator.split(`'`)[1]
                  this.metric_level_values[this.metric_level_values.length - 1].values.push(value_after_operator.split(`'`)[1])
                  this.metric_level_values[this.metric_level_values.length - 1].operators.push(this.getOperator(operator))
                } else if (value_after_operator == "None") {
                  this.metric_level_values[this.metric_level_values.length - 1].values.push(operator == "^" ? "False" : "True")
                  this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                    this.operators_for_dates.find((o) => o.value === "blank")
                  )
                } else {
                  if (value_after_operator.includes("today")) {
                    // this.metric_level_values[this.metric_level_values.length - 1].max_value = value_after_operator.split(`timedelta(`)[1].split(")")[0]
                    this.metric_level_values[this.metric_level_values.length - 1].values.push(
                      value_after_operator.split(`timedelta(`)[1].split(")")[0]
                    )
                    this.metric_level_values[this.metric_level_values.length - 1].operators.push(
                      this.getOperator(operator, value_after_operator)
                    )
                  } else {
                    // this.metric_level_values[this.metric_level_values.length - 1].max_value = value_after_operator
                    this.metric_level_values[this.metric_level_values.length - 1].values.push(value_after_operator)
                  }
                }
              } else {
                const both_ranges = metric_case_obj.when.split(" and ")

                const range_2 = both_ranges[1]
                const range_1 = both_ranges[0]

                const range_2_operator = range_2.slice(metric_name_end_pos, metric_name_end_pos + 1)[0]
                const range_1_operator = range_1.slice(metric_name_end_pos, metric_name_end_pos + 1)[0]
                const range_2_value = range_2.split(range_2_operator)[1]
                const range_1_value = range_1.split(range_1_operator)[1]

                const previous_both_ranges = case_obj.cases[i - 1].when.split(" and ")

                const previous_range_2 = previous_both_ranges[1]

                const previous_range_2_operator = previous_range_2?.slice(metric_name_end_pos, metric_name_end_pos + 1)[0]
                const previous_range_2_value = range_2.split(previous_range_2_operator)[1]

                const previous_range_1 = previous_both_ranges[0]

                const previous_range_1_operator = previous_range_1?.slice(metric_name_end_pos, metric_name_end_pos + 1)[0]
                const previous_range_1_value = previous_range_1.split(previous_range_1_operator)[1]

                if (i + 2 !== this.metric_levels) {
                  this.metric_level_values[this.metric_level_values.length - 1].values.push(parseInt(range_2_value))
                  this.metric_level_values[this.metric_level_values.length - 1].plotted_values.push(
                    this.metric_level_values[this.metric_level_values.length - 1].reversed
                      ? -1 * parseInt(range_2_value)
                      : parseInt(range_2_value)
                  )
                }

                if (operator == "=") {
                  if (!this.metrics_to_customize.includes(this.$utils.getMetricByReferenceName(metric_name).id)) {
                    should_customize = true
                  }
                }
                if (range_1_value !== (i - 1 !== 0 ? previous_range_2_value : previous_range_1_value)) {
                  if (!this.metrics_to_customize.includes(this.$utils.getMetricByReferenceName(metric_name).id)) {
                    should_customize = true
                  }
                }
              }
            }
          })

          if (should_customize) {
            setTimeout(() => {
              this.handleCustomizeClick(this.$utils.getMetricByReferenceName(metric_name), case_index)
            }, 400)
          }
        })

        this.all_values_filled = true
        this.max_and_min_values_filled = true
        this.scores_loaded = true
      })
    } else {
      this.scores_loaded = true
    }

    if (this.$route.query?.redirect_url) {
      this.add_aggregation = true
      this.aggregation_method = this.metrics_aggregation_methods[0].value

      this.score_object_type =
        this.$store.state.object_types.find((o) => this.$route.query?.redirect_url.includes(o.type))?.type || "company"
    }
  },

  methods: {
    handleWeightClick(metricIndex, weightIndex) {
      this.metric_weights[metricIndex] = weightIndex + 1
    },

    handlePercentageInput(e, i) {
      clearTimeout(this.percentage_input_timeout)
      this.percentage_input_timeout = setTimeout(() => {
        this.metric_percentages[i] = parseInt(e.target.value)
      }, 500)
    },
    handleAddLevel() {
      this.metric_levels += 1

      this.selected_metrics.forEach((m, i) => {
        this.metric_level_values[i].operators[0] = ">"
        this.handleMetricLevelMaxValueChange({ target: { value: this.metric_level_values[i].max_value } }, i, m.data_type == "Number")
        this.handleMetricLevelMinValueChange({ target: { value: this.metric_level_values[i].min_value } }, i, m.data_type == "Number")
      })
    },

    handleRemoveLevel() {
      this.metric_levels -= 1

      this.metric_level_values.forEach((metric_level, i) => {
        metric_level.plotted_values.splice(metric_level.plotted_values.length - 1, 1)
        metric_level.values.splice(metric_level.values.length - 1, 1)
        metric_level.connect.splice(metric_level.connect.length - 1, 1)

        this.handleMetricValues(metric_level.plotted_values, i, metric_level.reversed)
        this.handleMetricLevelMaxValueChange({ target: { value: this.metric_level_values[i].max_value } }, i, true)
        this.handleMetricLevelMinValueChange({ target: { value: this.metric_level_values[i].min_value } }, i, true)
      })
    },

    handleInput(e, v, p, v_2, p_2) {
      clearTimeout(this.input_timeout)

      this.input_timeout = setTimeout(() => {
        v[p] = e.target.value

        if (v_2) {
          v_2[p_2] = parseInt(e.target.value)
        }

        if (p == "max_value") {
          const plotted_values_calc = []
          v["plotted_values"].forEach((_, i) => {
            const value = v["min_value"] + ((v["max_value"] - v["min_value"]) / (v["plotted_values"].length + 1)) * (i + 1)
            plotted_values_calc.push(Math.round(value))
          })

          v["plotted_values"] = plotted_values_calc
          const plotted_values_calc_clone = this.$utils.clone(plotted_values_calc)
          plotted_values_calc_clone.push(v["max_value"])
          plotted_values_calc_clone.unshift(v["min_value"])
          v["values"] = plotted_values_calc_clone
        }
      }, 600)
    },
    chooseMetric(metric) {
      this.selected_metrics.push({ ...metric })
      setTimeout(() => {
        this.selected_metrics.forEach((m, i) => {
          this.metric_level_values[i].operators[0] = ">"

          this.handleMetricLevelMaxValueChange({ target: { value: m.data_type !== "Number" ? "" : 30000 } }, i, m.data_type == "Number")
          this.handleMetricLevelMinValueChange({ target: { value: m.data_type !== "Number" ? "" : 0 } }, i, m.data_type == "Number")
        })
      }, 500)
    },
    handleCancelClick() {
      if (this.$route.query?.redirect_url) {
        this.$emitter.emit(ALL_EMITTERS.SCORE_CREATED_FOR_MODAL, { score: null, page_reference: this.$route.params?.page_ref })
        this.$router.push(`${this.$route.query?.redirect_url}`)
      } else {
        this.$router.replace(`/admin/metrics?tab=scores`)
      }
    },
    handleCustomizeClick(metric, i) {
      const index = this.metrics_to_customize.findIndex((m) => m == metric.id)
      if (index == -1) {
        this.overlapping_values = []
        this.metrics_to_customize.push(metric.id)
        const values_alt = []
        values_alt[0] = {
          values: [this.metric_level_values[i].values[0]],
          operators: [this.metric_level_values[i].operators[this.metric_levels - 1]]
        }
        values_alt[this.metric_levels - 1] = {
          values: [this.metric_level_values[i].values[this.metric_level_values[i].values.length - 1]],
          operators: [this.metric_level_values[i].operators[0]]
        }
        this.metric_level_values[i].values.forEach((v, j) => {
          if (j) {
            let operators_calc = ""

            if (values_alt[0].operators[0] == "<") {
              //last value
              if (j + 2 == this.metric_levels) {
                operators_calc = [">=", "<="]
              } else {
                operators_calc = [">=", "<"]
              }
            } else if (values_alt[0].operators[0] == ">") {
              //last value
              if (j + 2 == this.metric_levels) {
                operators_calc = ["<=", ">="]
              } else {
                operators_calc = ["<=", ">"]
              }
            } else if (values_alt[0].operators[0] == "<=") {
              //last value
              if (j + 2 == this.metric_levels) {
                operators_calc = [">", "<"]
              } else {
                operators_calc = [">", "<="]
              }
            } else if (values_alt[0].operators[0] == ">=") {
              //last value
              if (j + 2 == this.metric_levels) {
                operators_calc = ["<", ">"]
              } else {
                operators_calc = ["<", ">="]
              }
            }

            values_alt[j] = { values: [values_alt[j - 1].values[values_alt[j - 1].values.length - 1], v], operators: operators_calc }
          }
        })

        this.metric_level_values[i].values_alt = values_alt
      } else {
        this.metrics_to_customize.splice(index, 1)
      }
    },

    getOperator(operator, value_after_operator, isnumber) {
      let operator_value = ""
      if (!value_after_operator) {
        if (operator == "=") {
          operator_value = "equal"
        }
        if (operator == "^") {
          operator_value = "not_equal"
        }
        if (operator == "%") {
          operator_value = "includes"
        }
      } else {
        if (value_after_operator.includes("+")) {
          if (operator == "=") {
            operator_value = "__DAYS_EXACT_DAYS_FROM_NOW__"
          } else if (operator == "$") {
            operator_value = "__DAYS_WITHIN_NEXT_X_DAYS__"
          } else if (operator == "@") {
            operator_value = "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__"
          }
        } else if (value_after_operator.includes(" - ")) {
          if (operator == "=") {
            operator_value = "__DAYS_EXACT_DAYS_AGO__"
          } else if (operator == "$") {
            operator_value = "__DAYS_WITHIN_LAST_X_DAYS__"
          } else if (operator == "@") {
            operator_value = "__DAYS_MORE_THAN_X_DAYS_AGO__"
          }
        } else if (value_after_operator.includes("-")) {
          if (operator == "=") {
            operator_value = "exact"
          } else if (operator == "$") {
            operator_value = "before"
          } else if (operator == "@") {
            operator_value = "after"
          }
        }
      }
      if (!value_after_operator) {
        return this.operators_for_strings.find((o) => o.value === operator_value)
      } else {
        if (operator == "~") {
          operator_value = ">="
        } else if (operator == "#") {
          operator_value = "<="
        } else if (operator == ">") {
          operator_value = "<"
        } else if (operator == "<") {
          operator_value = ">"
        }
        if (isnumber) {
          return this.operators_for_numbers.find((o) => o.value === operator_value).value
        } else {
          return this.operators_for_dates.find((o) => o.value === operator_value)
        }
      }
    },
    // handleStepClick(index) {
    //   this.steps.forEach((step, i) => {
    //     if (step.status == "current") {
    //       this.steps[i].status = "complete"
    //     }
    //   })
    //   this.steps[index].status = "current"
    // },
    /** Checks if all max and minimum values and operators are filled for 'Score Values' step. */

    handleCheckMaxAndMinValuesFilled() {
      this.max_and_min_values_filled = true
      this.metric_level_values.forEach((metric_level_value, i) => {
        if (this.selected_metrics[i]?.data_type === "Number") {
          if (!metric_level_value.operators[this.metric_levels - 1] || !metric_level_value.operators[0]) {
            this.max_and_min_values_filled = false
          }
          if (!metric_level_value.values[this.metric_levels - 2]) {
            this.max_and_min_values_filled = false
          } else if (!metric_level_value.values[0]) {
            this.max_and_min_values_filled = false
          } else if (parseInt(this.metric_levels) > 2) {
            if (
              (!metric_level_value.reversed && metric_level_value.values[this.metric_levels - 2] <= metric_level_value.values[0]) ||
              (metric_level_value.reversed && metric_level_value.values[this.metric_levels - 2] >= metric_level_value.values[0])
            ) {
              this.max_and_min_values_filled = false
            }
          }
        } else {
          if (!metric_level_value.values[this.metric_levels - 1] || !metric_level_value.operators[this.metric_levels - 1]) {
            this.max_and_min_values_filled = false
          }
          if (!metric_level_value.values[0] || !metric_level_value.operators[0]) {
            this.max_and_min_values_filled = false
          }
        }
      })
      this.handleCheckAllValuesFilled()
    },

    handleCheckAllValuesFilled() {
      this.all_values_filled = true
      const overlapping_values = []
      this.metric_level_values.forEach((metric_level_value, i) => {
        if (this.selected_metrics[i].data_type !== "Number") {
          for (let metric_level = 1; metric_level <= this.metric_levels; metric_level++) {
            if (!metric_level_value.values[metric_level - 1] || !metric_level_value.operators[metric_level - 1]) {
              this.all_values_filled = false
            }
          }
        }

        metric_level_value.values_alt?.forEach((v, j) => {
          const first_operator = v.operators[0]
          // const second_operator = v.operators[1]
          const first_value = v.values[0]
          // const second_value = v.values[1]
          const first_range_value = metric_level_value.values_alt[0].values[0]
          const first_range_operator = metric_level_value.values_alt[0].operators[0]

          if (j) {
            if (first_range_operator == ">") {
              if (
                first_operator == ">" ||
                first_operator == ">=" ||
                (first_operator == "==" && first_value > first_range_value) ||
                first_value > first_range_value
              ) {
                overlapping_values.push(
                  this.$t("For") +
                    ` ${this.selected_metrics[i]?.name} ` +
                    this.$t("metric, Level 1 covers all values greater than") +
                    ` ${first_range_value}, ` +
                    this.$t("so ranges for level") +
                    ` ${j + 1} ` +
                    this.$t("should be changed")
                )
              }
            }

            if (first_range_operator == ">=") {
              if (
                first_operator == ">" ||
                first_operator == ">=" ||
                (first_operator == "==" && first_value >= first_range_value) ||
                first_value >= first_range_value
              ) {
                overlapping_values.push(
                  this.$t("For") +
                    ` ${this.selected_metrics[i]?.name} ` +
                    this.$t("metric, Level 1 covers all values greater than or equal to") +
                    ` ${first_range_value}, ` +
                    this.$t("so ranges for level") +
                    ` ${j + 1} ` +
                    this.$t("should be changed")
                )
              }
            }

            if (first_range_operator == "<=") {
              if (
                first_operator == "<" ||
                first_operator == "<=" ||
                (first_operator == "==" && first_value <= first_range_value) ||
                first_value <= first_range_value
              ) {
                overlapping_values.push(
                  this.$t("For") +
                    ` ${this.selected_metrics[i]?.name} ` +
                    this.$t("metric, Level 1 covers all values less than or equal to") +
                    ` ${first_range_value}, ` +
                    this.$t("so ranges for level") +
                    ` ${j + 1} ` +
                    this.$t("should be changed")
                )
              }
            }

            if (first_range_operator == "<") {
              if (
                first_operator == "<" ||
                first_operator == "<=" ||
                (first_operator == "==" && first_value < first_range_value) ||
                first_value < first_range_value
              ) {
                overlapping_values.push(
                  this.$t("For") +
                    ` ${this.selected_metrics[i]?.name} ` +
                    this.$t("metric, Level 1 covers all values less than") +
                    ` ${first_range_value}, ` +
                    this.$t("so ranges for level") +
                    ` ${j + 1} ` +
                    this.$t("should be changed")
                )
              }
            }
          }

          this.overlapping_values = overlapping_values
        })
      })
      return this.all_values_filled
    },
    /**

 * @property {array} arr - array of values.
 * @property {string} type - object type (company,contact...).
 * @property {string} tab - which tab.
 *
 */
    handleMetricValues(arr, i, reversed) {
      let new_arr = null
      if (Array.isArray(arr)) {
        if (reversed) {
          new_arr = [...arr.map((v) => v * -1)]
        } else {
          new_arr = [...arr]
        }
      } else {
        if (reversed) {
          new_arr = [arr * -1]
        } else {
          new_arr = [arr]
        }
      }

      if (this.metric_level_values[i].plotted_values.length == new_arr.length) {
        this.metric_level_values[i].plotted_values = this.$utils.clone(new_arr)
      }

      new_arr.push(this.metric_level_values[i].values[this.metric_level_values[i].values.length - 1])
      new_arr.unshift(this.metric_level_values[i].values[0])

      this.metric_level_values[i].values = new_arr
    },

    // handleOperatorSelectMin(i, o) {
    //   this.metric_level_values[i].operators[0] = o
    //   if (this.metric_level_values[i].operators[0].value == "blank") {
    //     this.metric_level_values[i].values[0] = "True"
    //   }
    //   this.handleCheckMaxAndMinValuesFilled()
    // },
    handleOperatorSelect(i, j, o) {
      this.metric_level_values[i].operators[j] = o
      if (this.metric_level_values[i].operators[j].value == "blank") {
        this.metric_level_values[i].values[j] = "True"
      }
      this.handleCheckAllValuesFilled()
    },

    // handleNumberOperatorSelect(i, j, o, oi) {
    //   this.metric_level_values[i].values_alt[j].operators[oi] = o.value
    //   this.handleCheckAllValuesFilled()
    // },

    // handleInputPercentage(e, i) {
    //   this.$utils.debounce(() => {
    //     if (e.target.value > 0 && e.target.value < 100) {
    //       if (i < this.metric_percentages.length - 1 && e.target.value >= this.metric_percentages[i] + this.metric_percentages[i + 1]) {
    //         const backup = this.metric_percentages[i]
    //         this.metric_percentages[i] = 0
    //         this.metric_percentages[i] = backup

    //         //do another calculation instead
    //       } else if (
    //         i == this.metric_percentages.length - 1 &&
    //         e.target.value >= this.metric_percentages[i] + this.metric_percentages[i - 1]
    //       ) {
    //         const backup = this.metric_percentages[i]
    //         this.metric_percentages[i] = 0
    //         this.metric_percentages[i] = backup

    //         //do another calculation instead
    //       } else {
    //         if (i < this.metric_percentages.length - 1) {
    //           const diff = e.target.value - this.metric_percentages[i]
    //           this.metric_percentages[i] = parseInt(e.target.value)
    //           this.percentages_margin_left_array[i + 1] = this.percentages_margin_left_array[i + 1] + diff
    //           this.slider_handle_postions[i] = this.slider_handle_postions[i] + diff

    //           this.percentages_margin_left_array[i + 2] = this.percentages_margin_left_array[i + 2] + diff
    //           this.metric_percentages[i + 1] = this.metric_percentages[i + 1] - diff
    //         } else {
    //           const diff2 = e.target.value - this.metric_percentages[i]
    //           this.metric_percentages[i] = parseInt(e.target.value)
    //           this.percentages_margin_left_array[i + 1] = this.percentages_margin_left_array[i + 1] - diff2
    //           this.slider_handle_postions[i - 1] = this.slider_handle_postions[i - 1] - diff2
    //           this.percentages_margin_left_array[i] = this.percentages_margin_left_array[i] - diff2
    //           this.metric_percentages[i - 1] = this.metric_percentages[i - 1] - diff2
    //         }
    //       }
    //     } else {
    //       const backup = this.metric_percentages[i]
    //       this.metric_percentages[i] = 0
    //       this.metric_percentages[i] = backup
    //     }
    //   }, 850)
    // },

    /** Sets  */
    // handlePercentage(arr) {
    //   let arr_calc = null
    //   if (!arr[0]) {
    //     arr_calc = [arr]
    //   } else {
    //     arr_calc = arr
    //   }
    //   const new_percentages_array = []
    //   const new_percentages_margin_left_array = [0].concat(arr_calc)

    //   arr_calc.forEach((arr_value, i) => {
    //     if (!i) {
    //       new_percentages_array.push(arr_value)
    //     } else {
    //       new_percentages_array.push(arr_value - arr_calc[i - 1])
    //     }
    //   })
    //   new_percentages_array.push(100 - arr_calc[arr_calc.length - 1])
    //   this.metric_percentages = new_percentages_array
    //   this.percentages_margin_left_array = new_percentages_margin_left_array
    // },
    handleMetricLevelMaxValueChange(e, i, isNumber) {
      if (isNumber) {
        this.metric_level_values[i].values[this.metric_levels - 2] = e.target.value
        this.metric_level_values[i].max_value = e.target.value

        for (let j = 1; j < this.metric_levels - 2; j++) {
          this.metric_level_values[i].values[j] = ""
        }
      } else {
        this.metric_level_values[i].values[this.metric_levels - 1] = e.target.value
        for (let j = 1; j < this.metric_levels - 1; j++) {
          this.metric_level_values[i].values[j] = ""
        }
      }

      this.handleCheckMaxAndMinValuesFilled()
    },

    // handleMetricLevelValueChange(e, i, j) {
    //   this.metric_level_values[i].values[j] = e.target.value
    // },

    handleMetricLevelMinValueChange(e, i, isNumber) {
      this.metric_level_values[i].values[0] = e.target.value
      if (isNumber) {
        this.metric_level_values[i].min_value = e.target.value
        const range = parseInt(this.metric_level_values[i].values[this.metric_levels - 2]) - parseInt(this.metric_level_values[i].values[0])
        const increment = range / (this.metric_level_values[i].values.length - 1)
        this.metric_level_values[i].connect = [false]
        this.metric_level_values[i].values.forEach((m, j) => {
          if (!j || j == this.metric_level_values[i].values.length - 1) {
            this.metric_level_values[i].values[j] = parseInt(m)
          } else {
            this.metric_level_values[i].values[j] = Math.floor(parseInt(this.metric_level_values[i].values[j - 1]) + increment)
            this.metric_level_values[i].connect.push(false)
          }
        })
        this.metric_level_values[i].plotted_values = this.metric_level_values[i].values.slice(
          1,
          this.metric_level_values[i].values.length - 1
        )
        if (this.metric_level_values[i].reversed) {
          this.metric_level_values[i].plotted_values = this.metric_level_values[i].values
            .slice(1, this.metric_level_values[i].values.length - 1)
            .map((v) => v * -1)
        }
      }

      this.handleCheckMaxAndMinValuesFilled()
    },

    //FORMULA=[SUMPRODUCT,(,[,IFS,(GRR<95,1,AND(GRR>95,GRR<120),2), IFS(NRR<120,1,AND(NRR>120,NRR<150),2),IFS(USAGE=LOW,1,USAGE=HIGH,2)],[50,25,25])/SUM([50,25,25])]
    //add skipsteps as argument
    constructFormula(skipsteps) {
      const values_filled = this.handleCheckAllValuesFilled()

      if (values_filled) {
        let expression_string = "AVGSUMPRODUCT([{type:'cases',default:'0',cases:["
        // const metric_obj = {
        //   type: "cases",
        //   default: 0,
        //   cases: []
        // }

        this.metric_level_values.forEach((metric_level_value, metric_index) => {
          const metric_ref = this.selected_metrics[metric_index].reference_name
          const metric_data_type = this.selected_metrics[metric_index].data_type
          const cases = []

          if (metric_data_type === "Number") {
            const index = this.metrics_to_customize.findIndex((m) => m == this.selected_metrics[metric_index].id)
            if (index == -1) {
              for (let metric_level = 1; metric_level <= this.metric_levels; metric_level++) {
                if (metric_level == 1) {
                  const case_obj = { type: "case", when: "", then: "" }
                  case_obj.when = `${metric_ref}${metric_level_value.operators[this.metric_levels - 1]}${
                    this.metric_levels == 2 ? metric_level_value.max_value : metric_level_value.min_value
                  }`
                  case_obj.then = `${metric_level}`
                  cases.push(case_obj)
                } else if (metric_level == this.metric_levels) {
                  const case_obj = { type: "case", when: "", then: "" }
                  case_obj.when = `${metric_ref}${metric_level_value.operators[0]}${metric_level_value.values[metric_level - 2]}`
                  case_obj.then = `${metric_level}`
                  cases.push(case_obj)
                } else if (metric_level == 2) {
                  const case_obj = { type: "case", when: "", then: "" }
                  case_obj.when = `${metric_ref}${
                    metric_level_value.operators[0] == "<="
                      ? "<"
                      : metric_level_value.operators[0] == ">="
                        ? ">"
                        : metric_level_value.operators[0]
                  }${metric_level_value.operators[0].includes("=") ? "" : "="}${
                    this.metric_levels == 2 ? metric_level_value.max_value : metric_level_value.min_value
                  } and ${metric_ref}${
                    metric_level + 1 == this.metric_levels
                      ? metric_level_value.operators[this.metric_levels - 1] == "<="
                        ? "<"
                        : metric_level_value.operators[this.metric_levels - 1] == ">="
                          ? ">"
                          : metric_level_value.operators[this.metric_levels - 1]
                      : metric_level_value.operators[this.metric_levels - 1]
                  }${!metric_level_value.operators[0].includes("=") && metric_level + 1 == this.metric_levels ? "=" : ""}${
                    metric_level_value.values[metric_level - 1]
                  }`
                  case_obj.then = `${metric_level}`
                  cases.push(case_obj)
                } else {
                  const case_obj = { type: "case", when: "", then: "" }
                  case_obj.when = `${metric_ref}${
                    metric_level_value.operators[0] == "<="
                      ? "<"
                      : metric_level_value.operators[0] == ">="
                        ? ">"
                        : metric_level_value.operators[0]
                  }${metric_level_value.operators[0].includes("=") ? "" : "="}${
                    metric_level_value.values[metric_level - 2]
                  } and ${metric_ref}${
                    metric_level + 1 == this.metric_levels
                      ? metric_level_value.operators[this.metric_levels - 1] == "<="
                        ? "<"
                        : metric_level_value.operators[this.metric_levels - 1] == ">="
                          ? ">"
                          : metric_level_value.operators[this.metric_levels - 1]
                      : metric_level_value.operators[this.metric_levels - 1]
                  }${!metric_level_value.operators[0].includes("=") && metric_level + 1 == this.metric_levels ? "=" : ""}${
                    metric_level_value.values[metric_level - 1]
                  }`
                  case_obj.then = `${metric_level}`
                  cases.push(case_obj)
                }
              }
            } else {
              metric_level_value.values_alt.forEach((value, j) => {
                const case_obj = { type: "case", when: "", then: "" }
                case_obj.when = `${metric_ref}${value.operators[0]}${value.values[0]}${
                  value.operators[1] ? ` and ${metric_ref}${value.operators[1]}${value.values[1]}` : ""
                }`
                case_obj.then = `${j + 1}`
                cases.push(case_obj)
              })
            }
          } else {
            for (let metric_level = 1; metric_level <= this.metric_levels; metric_level++) {
              const case_obj = { type: "case", when: "", then: "" }

              if (metric_data_type == "String") {
                case_obj.when = `${metric_ref} ${
                  metric_level_value.operators[metric_level - 1].value === "equal" ||
                  (metric_level_value.operators[metric_level - 1].value === "blank" &&
                    metric_level_value.values[metric_level - 1] === "True")
                    ? "=="
                    : metric_level_value.operators[metric_level - 1].value === "not_equal" ||
                        metric_level_value.operators[metric_level - 1].value === "not_includes" ||
                        (metric_level_value.operators[metric_level - 1].value === "blank" &&
                          metric_level_value.values[metric_level - 1] === "False")
                      ? "!="
                      : metric_level_value.operators[metric_level - 1].value === "includes"
                        ? "in"
                        : ""
                } '${
                  metric_level_value.operators[metric_level - 1].value === "blank" ? "None" : metric_level_value.values[metric_level - 1]
                }'`
              }

              if (metric_data_type == "Date") {
                let operator_symbol = ""
                let condition_string = ""

                if (
                  metric_level_value.operators[metric_level - 1].value == "after" ||
                  metric_level_value.operators[metric_level - 1].value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
                  metric_level_value.operators[metric_level - 1].value == "__DAYS_MORE_THAN_X_DAYS_AGO__"
                ) {
                  operator_symbol = ">"
                }
                if (
                  metric_level_value.operators[metric_level - 1].value == "before" ||
                  metric_level_value.operators[metric_level - 1].value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
                  metric_level_value.operators[metric_level - 1].value == "__DAYS_WITHIN_NEXT_X_DAYS__"
                ) {
                  operator_symbol = "<"
                }
                if (
                  (metric_level_value.operators[metric_level - 1].value === "blank" &&
                    metric_level_value.values[metric_level - 1] === "True") ||
                  metric_level_value.operators[metric_level - 1].value == "exact" ||
                  metric_level_value.operators[metric_level - 1].value == "__DAYS_EXACT_DAYS_AGO__" ||
                  metric_level_value.operators[metric_level - 1].value == "__DAYS_EXACT_DAYS_FROM_NOW__"
                ) {
                  operator_symbol = "=="
                }
                if (
                  metric_level_value.operators[metric_level - 1].value === "blank" &&
                  metric_level_value.values[metric_level - 1] === "False"
                ) {
                  operator_symbol = "!="
                }

                if (metric_level_value.operators[metric_level - 1].value.includes("__")) {
                  condition_string = `today() ${
                    metric_level_value.operators[metric_level - 1].value.includes("AHEAD") ? "+" : "-"
                  } timedelta(${String(metric_level_value.values[metric_level - 1]).trim()})`
                } else {
                  condition_string =
                    metric_level_value.operators[metric_level - 1].value === "blank" ? "None" : metric_level_value.values[metric_level - 1]
                }
                case_obj.when = `${metric_ref} ${operator_symbol} ${condition_string}`
              }

              case_obj.then = `${metric_level}`
              cases.push(case_obj)
            }
          }
          const metric_weight_values = [0.25, 0.5, 1, 1.5, 2]
          cases.forEach((c, i) => {
            expression_string += JSON.stringify(c)
            if (i + 1 == cases.length) {
              if (metric_index + 1 != this.metric_level_values.length) {
                expression_string += "]},{type:'cases',default:'0',cases:["
              } else {
                expression_string += `]}],[${
                  this.weight_option == "Percentage" ? this.metric_percentages : this.metric_weights.map((m) => metric_weight_values[m - 1])
                }])`
              }
            } else {
              expression_string += ","
            }
          })
        })

        // let expression_string = "AVGSUMPRODUCT(["

        // expression_string += `${weights.join(",")}])`

        const metric_obj = {
          ...this.score,
          is_score_logic_reversed: this.ascending_levels,
          aggregation_method: this.aggregation_method,
          metric_class: "score_metric",
          formulary: { type: "value", expression: expression_string },
          object_type: this.score_object_type,
          name: this.score_name,
          description: this.score_description
        }
        metric_obj.id = this.$route.params.sid
        if (this.score_edit) {
          this.$store.dispatch("data/updateOrganizationMetric", metric_obj).then(() => {
            this.nextClick(4)

            if (skipsteps) {
              for (let position = 0; position <= 5; position++) {
                this.steps[position].status = "complete"
              }
              this.steps[5].status = "current"
            }
            this.$store.dispatch("ui/hideLoading")
          })
        } else {
          this.$store.dispatch("ui/showLoading")
          this.$store.dispatch("data/addMetric", metric_obj).then((res) => {
            if (this.$route.query?.redirect_url) {
              this.$store.dispatch("ui/hideLoading")
              this.$emitter.emit(ALL_EMITTERS.SCORE_CREATED_FOR_MODAL, { score: res, page_reference: this.$route.params?.page_ref })
              this.$router.push(`${this.$route.query?.redirect_url}`)
            }
            this.$store.dispatch("ui/hideLoading")
            this.nextClick(4)
          })
        }

        // const new_metric = { name: this.score_name, metric_class: "score_metric", formula: formula, metric_type: "t", description: this.score_description, is_score_logic_reversed: this.ascending_levels }
        // new_metric.id = this.$route.params.sid
        // if (this.score_edit) {
        //   this.$store.dispatch("ui/showLoading")

        //   this.$store.dispatch("data/updateOrganizationMetric", new_metric).then(() => {

        //     this.nextClick(5)

        //     if (skipsteps) {
        //       for (let position = 0; position <= 5; position++) {
        //         this.steps[position].status = "complete"
        //       }
        //       this.steps[6].status = "current"
        //     }
        //     this.$store.dispatch("ui/hideLoading")
        //   })

        // } else {
        //   this.$store.dispatch("ui/showLoading")
        //   this.$store.dispatch("data/addMetric", new_metric).then(() => {
        //     //DONE
        //     this.nextClick(5)
        //     this.$store.dispatch("ui/hideLoading")
        //   })
        // }
      }
    },
    // handleWeightClick(metricIndex, weightIndex) {
    //   this.metric_weights[metricIndex] = weightIndex + 1
    // },

    nextClick(position) {
      this.steps[position].status = "complete"
      if (position == 1) {
        this.steps[position + 1].status = "current"
      } else {
        this.steps[position + 1].status = "current"
      }
    },

    // submitClick() {
    //   this.constructFormula(true)
    // },

    backClick(position) {
      this.steps[position].status = ""

      this.steps[position - 1].status = "current"
    }
  }
}
</script>

<style src="@vueform/slider/themes/default.css"></style>

<style>
.progress-ring {
  fill: transparent;
  stroke: #e0e0e0;
  stroke-width: 5;
}

.progress-value {
  fill: transparent;
  stroke: #4a90e2;
  stroke-width: 5;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  transition: stroke-dashoffset 0.3s;
}
</style>
